<template>
  <v-container id="detailPropertyView">
    <div
      v-if="homeInfoLoaded"
      class="main-grid"
      :class="{ 'not-rent': homeInfo.PropertyType !== 'ResidentialLease' }"
    >
      <div class="imageWrap">
        <div class="image">
          <template v-if="!totalImages">
            <div class="corner-strip">
              <span>No Images</span>
            </div>
          </template>
          <v-carousel
            hide-delimiters
            height="400"
            :cycle="false"
            :class="{ 'hidden-icons': !totalImages }"
            @change="onArrowClick"
          >
            <v-carousel-item
              v-for="(image, index) in propertyImages"
              :key="index"
              :src="image"
              :lazy-src="'/img/gradient-image-loader.png'"
            >
              <template v-slot:placeholder>
                <v-layout fill-height align-center justify-center ma-0>
                  <v-progress-circular indeterminate color="grey darken-1" />
                </v-layout>
              </template>
            </v-carousel-item>
          </v-carousel>
          <v-card
            v-if="totalImages"
            id="textCard"
            tile
            :class="positionFix"
            color="#00000080"
          >
            <div
              class="image-number-indicator"
              v-text="`${currentIndex}/${totalImages}`"
            />
          </v-card>
        </div>
        <div
          v-if="profileStatus === 'client'"
          style="margin-top: 1rem;"
          class="inside-image"
        >
          <div
            v-show="!isTourRequested"
            class="favorite"
            :class="isFavProperty ? 'fav-added' : 'fav-standard'"
            @click.stop="isFavProperty ? removeFavorite() : setFavorite()"
          >
            <v-btn id="assignToBtn" icon depressed large flat>
              <v-icon large>
                fas fa-heart
              </v-icon>
            </v-btn>
            <span>{{
              isFavProperty ? "Added as Favorite" : "Add as Favorite"
            }}</span>
          </div>
          <div
            v-show="isFavProperty && isActive"
            class="request"
            :class="
              isTourRequested ? 'property-requested' : 'property-standard'
            "
            @click.stop="isTourRequested ? null : requestTour()"
          >
            <v-btn
              id="assignToBtn"
              :class="{ 'property-requested': isTourRequested }"
              icon
              depressed
              large
              flat
            >
              <v-icon large>
                fas fa-paper-plane
              </v-icon>
            </v-btn>
            <span>
              {{ isTourRequested ? "Tour Requested" : "Request Tour" }}
            </span>
          </div>
        </div>
        <div v-if="!isMobile" class="button-wrapper">
          <v-btn
            v-if="displayAssignBtn"
            class="action-button white--text"
            :color="colors.hwayzBrandBlue"
            @click.stop="setAssignModal(true)"
          >
            Assign to Client
          </v-btn>
          <v-btn
            v-if="displayAssignBtn"
            class="action-button white--text"
            :color="colors.hwayzBrandBlue"
            @click.stop="setAgentRequest()"
          >
            Request Tour
          </v-btn>
          <v-btn
            v-if="!propertyObjectId"
            class="action-button white--text"
            :color="colors.hwayzBrandBlue"
            @click.stop="backToSpecificPage()"
          >
            Go back
          </v-btn>
        </div>
      </div>
      <div class="information">
        <h3 class="detail-property-view__title">
          <strong>{{ homeInfo.Address ? getCustomAddress() : "" }}</strong>
        </h3>
        <div
          class="text-sm-left status-info"
          :class="getStatusClass(homeInfo['Status'])"
        >
          {{ homeInfo["Status"] }}
        </div>
        <div v-if="isMobile" style="margin: 1rem 0rem;">
          <v-btn
            v-if="displayAssignBtn"
            class="action-button white--text"
            :color="colors.hwayzBrandBlue"
            @click.stop="setAssignModal(true)"
          >
            Assign to Client
          </v-btn>
          <v-btn
            v-if="displayAssignBtn"
            class="action-button white--text"
            :color="colors.hwayzBrandBlue"
            @click.stop="setAgentRequest()"
          >
            Request Tour
          </v-btn>
          <v-btn
            v-if="!propertyObjectId"
            class="action-button white--text"
            :color="colors.hwayzBrandBlue"
            @click.stop="backToSpecificPage()"
          >
            Go back
          </v-btn>
        </div>
        <property-view-section
          :section-data="getPropertyTopSection()"
          :property-details="homeInfo"
        />
      </div>

      <div class="sections">
        <v-container v-for="(params, index) in sectionParams" :key="index">
          <property-view-section
            :section-params="sectionParams[index]"
            :section-data="getPropertySectionData(index)"
            :property-details="homeInfo"
          />
        </v-container>
        <div>
          <v-btn
            v-if="displayAssignBtn"
            class="action-button white--text"
            :color="colors.hwayzBrandBlue"
            @click.stop="setAssignModal(true)"
          >
            Assign to Client
          </v-btn>
          <v-btn
            v-if="displayAssignBtn"
            class="action-button white--text"
            :color="colors.hwayzBrandBlue"
            @click.stop="setAgentRequest()"
          >
            Request Tour
          </v-btn>
          <v-btn
            v-if="!propertyObjectId && !isMobile"
            class="action-button white--text"
            :color="colors.hwayzBrandBlue"
            @click.stop="backToSpecificPage()"
          >
            Go back
          </v-btn>
        </div>
      </div>
      <div v-if="latLng.length > 0" class="map">
        <google-location-map
          :key="componentKey"
          :map-markers="latLng"
          :map-zoom="16"
        />
      </div>
      <preloader v-else :loading-message="'Loading Location map...'" />
    </div>
    <full-screen-spinner
      v-else
      spinner-text="Loading Property details..."
      top-margin="0px"
    />
    <client-list-menu
      v-if="profileStatus === 'agent'"
      :show-modal="displayAssignModal"
      :selected-property="homeInfo"
      :associated-clients="associatedClients"
      :set-request="clientRequestTour"
      @assign-client-to-property="assignProperty($event)"
      @close-modal="closeModal()"
    />
    <content-with-button-modal
      :modal-params="modalParams"
      :show-modal="displayFavWarning"
      @close-modal="displayFavWarning = false"
    />
  </v-container>
</template>

<script>
import { mapState, mapMutations, mapActions } from 'vuex'
import moment from 'moment'
import refreshContent from '../../mixins/refreshContent'
import refreshClientData from '../../mixins/refreshClientData'
import PropertyViewSection from './PropertyViewSection.vue'
import ClientListMenu from '../ClientListMenu.vue'
import Preloader from '../../uiUtils/Preloader.vue'
import FullScreenSpinner from '../../uiUtils/FullScreenSpinner.vue'
import GoogleLocationMap from '../../googlemap/GoogleLocationMap.vue'
import ContentWithButtonModal from '../../modals/ContentWithButtonModal.vue'
import {
  getPropertyById,
  requestPropertyTour,
  removeFromClientFavorite,
  setPropertyasClientFavorite
} from '../../../services/propertiesService'
import { isEmpty, sortNames } from '../../../utils/generalUtils'
import {
  propertyFullAddress,
  getClientFavFromPropertyId
} from '../../../utils/propertyUtils'
import { HIDE_STATUSES } from '../../common/searchPropertyConst'
import { activeRouteHomeIds } from '../../../services/routeService'

import colors from '../../../scss/_colors.scss'

export default {
  components: {
    PropertyViewSection,
    ClientListMenu,
    Preloader,
    FullScreenSpinner,
    GoogleLocationMap,
    ContentWithButtonModal
  },

  mixins: [refreshContent, refreshClientData],

  props: {
    propertyObjectId: {
      type: String,
      default: null
    }
  },

  data() {
    return {
      displayAssignModal: false,
      associatedClients: [],
      homeInfo: {},
      latLng: [],
      componentKey: 0,
      colors,
      currentIndex: 1,
      activeRouteHomeIds: [],
      clientRequestTour: false
    }
  },

  computed: {
    ...mapState('common', ['searchObject', 'isMobile']),
    ...mapState('home', ['activeRoute']),

    isFavProperty() {
      if (this.clientFavorites) {
        return this.clientFavorites.some(
          home => home._id === this.homeInfo._id
        )
      }
      return false
    },

    isTourRequested() {
      if (!isEmpty(this.clientData) && this.homeInfo) {
        const homeObject = getClientFavFromPropertyId(
          this.clientFavorites,
          this.homeInfo._id
        )
        return homeObject && homeObject.requestTour === true
      }
      return false
    },

    isLeaseProperty() {
      return this.homeInfo.PropertyType === 'ResidentialLease'
    },

    isActive() {
      return !HIDE_STATUSES.includes(this.homeInfo.Status)
    },

    homeInfoLoaded() {
      return !isEmpty(this.homeInfo)
    },

    displayAssignBtn() {
      return (
        this.profileStatus === 'agent' &&
        this.homeInfo &&
        !HIDE_STATUSES.includes(this.homeInfo.Status)
      )
    },

    totalImages() {
      return this.homeInfo.Media ? this.homeInfo.Media.length : null
    },

    propertyImages() {
      if (this.homeInfo.Media && this.homeInfo.Media.length > 0) {
        return this.homeInfo.Media.map(m => m.MediaURL)
      } else {
        return ['/img/gradient-image-loader.png']
      }
    },

    sectionParams() {
      let tabs = [
        { title: 'Property Attributes' },
        { title: 'General Information' },
        { title: 'Utility Information' },
        { title: 'Financial Information' },
        { title: 'Remarks' }
      ]

      if (this.profileStatus === 'agent') {
        tabs = [
          ...tabs,
          ...[
            { title: this.isLeaseProperty ? 'How to Show' : 'Property access' },
            { title: 'AGENT INFO' },
            { title: 'OWNER INFO' },
            { title: 'FEES' }
          ]
        ]
      }

      if (this.isLeaseProperty) {
        tabs = [...tabs, { title: 'How to Apply' }]
        if (this.profileStatus === 'agent') {
          tabs = [...tabs, { title: 'Office Information' }]
        }
      }

      return tabs
    },

    positionFix() {
      return this.profileStatus === 'client' ? 'client-fix' : 'agent-fix'
    }
  },

  watch: {
    clientData: {
      handler: function(value) {
        this.activeRouteHomeIds = activeRouteHomeIds(value)
      },
      deep: true
    },

    homeInfo: {
      handler: function(newValue) {
        if (!isEmpty(newValue)) {
          this.latLng = [
            {
              position: {
                lat: newValue.Latitude,
                lng: newValue.Longitude
              },
              address: propertyFullAddress(newValue)
            }
          ]
          this.componentKey++
        }
      },
      deep: true
    }
  },

  created() {
    window.scrollTo(0, 0)
    if (this.$route.params.homeId) {
      if (this.$route.path.includes('favorites')) {
        this.getPropertyDetails(this.$route.params.homeId, 'favorites')
      } else {
        if (this.searchObject.data && this.searchObject.data.length > 0) {
          this.homeInfo = this.searchObject.data.find(
            home => home._id === this.$route.params.homeId
          )
        } else {
          this.getPropertyDetails(this.$route.params.homeId, 'search')
        }
      }
    } else if (this.propertyObjectId) {
      this.getPropertyDetails(this.propertyObjectId, null)
    }

    if (this.agentData) {
      this.associatedClients = sortNames(this.agentData.clients)
    }
  },

  mounted() {
    this.setShowingMobileBackButton(true)
    this.activeRouteHomeIds = activeRouteHomeIds(this.clientData)
  },

  destroyed() {
    if (
      this.activeRoute.name !== 'client-schedule' &&
      this.activeRoute.name !== 'calendar' &&
      this.activeRoute.name !== 'communication' &&
      this.activeRoute.name !== 'client-communication'
    ) {
      this.setShowingMobileBackButton(false)
    }
  },

  methods: {
    ...mapMutations('mobile', {
      setShowingMobileBackButton: 'SET_SHOWING_MOBILE_BACK_BUTTON'
    }),
    ...mapMutations('common', {
      setDoScroll: 'SET_DO_SCROLL'
    }),
    ...mapActions('client', ['updateClientPropertiesAction']),

    onArrowClick(value) {
      setTimeout(() => {
        this.currentIndex = value + 1
      }, 200)
    },

    async getPropertyDetails(homeId, section) {
      const response = await getPropertyById(homeId)
      if (response.status === 200) {
        const clientFav = this.clientData
          ? this.clientData.homes.find(fav => fav.propertyId === homeId)
          : undefined
        if (section === 'favorites' && clientFav) {
          this.homeInfo = {
            ...response.data,
            ...clientFav,
            favoriteId: clientFav._id,
            _id: response.data._id
          }
        } else {
          this.homeInfo = { ...response.data }
        }
      } else if (section) {
        // Entered a wrong home id so redirect to search/favorite
        this.$router.push({ name: `${this.profileStatus}-${section}` })
      }
    },

    // Set property as favorite for client property details
    async setFavorite() {
      const updatedClientProperties = await setPropertyasClientFavorite(
        this.clientData,
        this.clientFavorites,
        this.homeInfo
      )
      if (updatedClientProperties) {
        this.homeInfo = updatedClientProperties.updatedClientFavs.find(
          home => home._id === this.homeInfo._id
        )
        this.updateClientPropertiesAction(updatedClientProperties)
      }
    },

    // Remove property from client favorite list
    async removeFavorite() {
      const updatedClientProperties = await removeFromClientFavorite(
        this.clientData,
        this.clientFavorites,
        this.homeInfo
      )
      if (updatedClientProperties) {
        this.updateClientPropertiesAction(updatedClientProperties)
      }
    },

    // Request tour for a property
    async requestTour() {
      const updatedClientProperties = await requestPropertyTour(
        this.clientData,
        this.clientFavorites,
        this.homeInfo
      )
      if (updatedClientProperties) {
        this.updateClientPropertiesAction(updatedClientProperties)
        this.homeInfo = { ...this.homeInfo, requestTour: true }
      }
    },

    backToSpecificPage() {
      if (this.$route.path.includes('favorites')) {
        this.setDoScroll(true)
        this.$router.push({ name: 'client-favorites' })
      } else {
        this.$router.go(-1)
      }
    },

    getCustomAddress() {
      return `${this.homeInfo.Address}, ${this.homeInfo.City}, ${this.homeInfo.StateOrProvince} ${this.homeInfo.PostalCode}`
    },

    assignProperty(selectedClient) {
      this.setAssignModal(false)
      if (this.clientRequestTour) {
        this.requestTourForClient(this.homeInfo, selectedClient)
      } else {
        this.assignPropertyToClient(this.homeInfo, selectedClient)
      }
    },

    setAssignModal(status) {
      this.displayAssignModal = status
    },

    setAgentRequest() {
      this.clientRequestTour = true
      this.setAssignModal(true)
    },

    closeModal() {
      this.clientRequestTour = false
      this.setAssignModal(false)
    },

    getPropertySectionData(sectionNum) {
      let returnValue = []
      switch (sectionNum) {
        case 0:
          return [
            { text: 'Address', key: 'Address' },
            // { text: 'MLS Number', key: 'MLSNumber' },
            // { text: 'List Price', value: this.getListPrice() },
            { text: 'County', key: 'CountyOrParish' },
            { text: 'Subdivision', key: 'SubdivisionName' },
            // { text: 'Bed Bath Count', value: this.getBedBathCount() },
            // { text: 'Living Room(s)', key: 'NumLiving' },
            // { text: 'Dining Room(s)', key: 'NumDining' },
            // { text: 'Area', value: this.getArea() },
            { text: 'Year Built', key: 'YearBuilt' },
            { text: 'Sub Type', key: 'PropertySubType' },
            { text: 'ISD/School District', key: 'HighSchoolDistrict' },
            { text: 'Elementary School', key: 'ElementarySchool' },
            { text: 'Middle/Jr School', key: 'MiddleOrJuniorSchool' },
            // { text: 'Jr High School', key: 'MiddleIntermediateSchool' },
            { text: 'High School', key: 'HighSchool' }
          ]
        case 1:
          returnValue = [
            { text: 'Parking', key: 'ParkingFeatures' },
            { text: 'Lot Size', key: 'LotSizeArea' },
            { text: 'Lot Features', key: 'LotFeatures' },
            { text: 'Levels', key: 'Levels' },
            // { text: 'Rooms', key: 'Rooms' },
            { text: 'Acres', key: 'LotSizeAcres' },
            { text: 'Pool', key: 'PoolFeatures' },
            // { text: 'Dining', key: 'DiningDescription' },
            { text: 'Amenities', key: 'AssociationAmenities' },
            // { text: 'Master on Main', value: this.getYesNo('MasterMain') },
            { text: 'Interior', key: 'InteriorFeatures' },
            { text: 'Exterior', key: 'ExteriorFeatures' },
            { text: 'Flooring', key: 'Flooring' },
            { text: 'Kitchen', key: 'Kitchen' },
            { text: 'Appliances', key: 'Appliances' },
            { text: 'View', key: 'View' },
            { text: 'Foundation', key: 'FoundationDetails' }
          ]

          if (this.isLeaseProperty) {
            returnValue.push({
              header: 'Pet Options',
              value: {
                tableHeaders: [
                  { text: '', value: '', sortable: false, width: '40%' },
                  { text: '', value: '', sortable: false, width: '60%' }
                ],
                tableValues: [
                  { text: 'Pets', value: this.getYesNo('Pets') },
                  {
                    text: 'Pet Deposit',
                    value: this.getAmountValue('PetDeposit')
                  },
                  { text: 'Per Pet', value: this.getYesNo('PerPet') },
                  {
                    text: 'Additional Pet Fee',
                    value: `${this.getAmountValue('AdditionalPetFee')} / ${
                      this.homeInfo.AdditionalPetFeeDescript
                    }`
                  },
                  {
                    text: 'Monthly Pet Rent',
                    value: this.getAmountValue('MonthlyPetRent')
                  },
                  {
                    text: 'Monthly Pet Rent Per Pet',
                    value:
                      this.getYesNo('PerPet') === 'Yes' &&
                      this.homeInfo.MonthlyPetRent
                        ? 'Yes'
                        : 'No'
                  },
                  { text: 'Pet Description', key: 'PetDescription' }
                ]
              }
            })
            returnValue.push(
              ...[
                { text: 'Smoking Inside', key: 'SmokingInside' },
                { text: 'Laundry Feature', key: 'LaundryFacilities' },
                { text: 'Dryer', key: 'Dryer' },
                { text: 'Dryer Maintained', key: 'DryerMaintained' },
                { text: 'Appliances/Equipments', key: 'KitchenAppliances' },
                { text: 'Range', key: 'Range' },
                { text: 'Washer', key: 'Washer' },
                { text: 'Washer Maintained', key: 'WasherMaintained' },
                { text: 'Refrigerator', key: 'Refrigerator' },
                {
                  text: 'Refrigerator Maintained',
                  key: 'RefrigeratorMaintained'
                }
              ]
            )
          }
          // returnValue.push({
          //   text: 'Last Modified',
          //   value: this.getDate('ModificationTimestamp')
          // })
          break
        case 2:
          returnValue = [
            { text: 'Heat', key: 'Heating' },
            { text: 'AC', key: 'Cooling' },
            { text: 'Utilities', key: 'Utilities' },
            { text: 'Sewer', key: 'Sewer' },
            { text: 'Water', key: 'WaterSource' }
          ]
          if (this.isLeaseProperty) {
            returnValue.push(
              ...[
                { text: 'Tenant Pays', key: 'TenantPays' },
                { text: 'Owner Pays', key: 'OwnerPays' }
              ]
            )
          }
          break
        case 3:
          return [
            { text: 'HOA Name', key: 'AssociationName' },
            { text: 'HOA Fee', key: 'AssociationFee' },
            { text: 'HOA Fee Frequency', key: 'AssociationFeeFrequency' },
            { text: 'Exclusions', key: 'Exclusions' },
            { text: 'Financing', key: 'BuyerFinancing' },
            // TODO: { text: 'Tax Rate', value: this.getTaxRate() },
            { text: 'Estimated Tax', key: 'TaxAnnualAmount' }
            // TODO: { text: 'REO/Foreclosure', value: this.getYesNo('ForeclosureREO') }
          ]
        case 4:
          return [{ text: 'Public remarks', key: 'PublicRemarks' }]
        case 5:
          returnValue = [
            { text: 'Show', key: 'ShowingInstructions' },
            // { text: 'Access', key: 'AccessInstruction' },
            { text: 'Agent Remarks', key: 'PrivateRemarks' },
            { text: 'Status', key: 'Status' },
            {
              text: 'Lockbox Info',
              value: this.contactStrings('/', 'LockBoxType', 'LockBoxLocation')
            },
            { text: 'Security', key: 'SecurityFeatures' },
            { text: 'Directions', key: 'Directions' }
          ]
          if (this.isLeaseProperty) {
            returnValue = [
              { text: 'Show', key: 'ShowingInstructions' },
              { text: 'Access', key: 'AccessInstruction' },
              {
                text: 'Lockbox',
                value: this.contactStrings(
                  '/',
                  'LockboxType',
                  'LockboxLocation'
                )
              },
              { text: 'Security', key: 'Security' },
              { text: 'Occupant Type', key: 'OccupantType' },
              { text: 'Occupant Phone', key: 'OccupantPhone' },
              { text: 'Other Phone', key: 'OtherPhone' },
              { text: 'Management Co.', key: 'ListOfficeName' },
              { text: 'Management Phone', key: 'ListOfficePhone' },
              { text: 'Directions', key: 'Directions' },
              { text: 'Remarks', key: 'PrivateRemarks' }
            ]
          }
          break
        case 6:
          return [
            { text: 'Listing Agent', key: 'ListAgentFullName' },
            { text: 'Agent Email', key: 'ListAgentEmail' },
            { text: 'Listing Office', key: 'ListOfficeName' },
            { text: 'Agent Phone Number', key: 'ListAgentPhone' }
          ]
        case 7:
          return [
            { text: 'Owner Name', key: 'OwnerName' },
            { text: 'Owner Phone', key: 'OwnerPhone' }
          ]
        case 8:
          return [
            { text: 'Sub Agency Compensation', value: `${this.homeInfo['SubAgencyCompensation']}${this.homeInfo['SubAgencyCompensationType']}` },
            { text: 'Buyer Agency Compensation', value: `${this.homeInfo['BuyerAgencyCompensation']}${this.homeInfo['BuyerAgencyCompensationType']}` },
            // { text: 'Bonus', key: 'Bonus' },
            {
              text: 'Listing date',
              value: this.getDate('ListingContractDate')
            },
            {
              text: 'OLP (Old List Price)',
              value: `$ ${this.homeInfo['OriginalListPrice']}`
            },
            { text: 'ADOM', key: 'DaysOnMarket' },
            { text: 'CDOM', key: 'CumulativeDaysOnMarket' }
          ]
        case 9:
          return [
            {
              text: 'Application Fee',
              value: this.getAmountValue('ApplicationFee')
            },
            {
              text: 'Security Deposit',
              value: this.getAmountValue('SecurityDeposit')
            },
            {
              text: 'Administration Fee',
              value: this.getAmountValue('AdministrationFee')
            },
            {
              text: 'Availability',
              value: this.getDate('OriginalEntryTimestamp')
            },
            { text: 'Application Policy', key: 'ApplicationPolicy' },
            { text: 'Managed By', key: 'ManagedBy' },
            { text: 'App Instructions', key: 'OnlineAppInstructions' },
            {
              text: 'App Instructions Public',
              key: 'OnlineAppInstructionsPublic'
            },
            { text: 'Per Person', value: this.getYesNo('PerPerson') },
            {
              text: 'First Month Rent Payable To',
              key: 'FirstMonthRentPayableTo'
            },
            { text: 'Lease Guarantor', value: this.getYesNo('LeaseGuarantor') },
            { text: 'Application Required', key: 'ApplicationRequired' },
            { text: 'Certified Funds', value: this.getYesNo('CertifiedFunds') },
            { text: 'Minimum Lease Months', key: 'MinLeaseMonths' },
            { text: 'Maximum Lease Months', key: 'MaxLeaseMonths' },
            { text: 'Lease Length Negotiable', key: 'LseLengthNegotiable' }
          ]
        case 10:
          return [
            {
              text: 'List Agent',
              value: this.contactStrings(
                '/',
                'ListAgentMLSID',
                'ListAgentFullName'
              )
            },
            { text: 'Agent Phone', key: 'ListAgentDirectWorkPhone' },
            {
              text: 'List Agent 2',
              value: this.contactStrings(
                '/',
                'CoListAgentMLSID',
                'CoListAgentFullName'
              )
            },
            { text: 'Agent 2 Phone', key: 'CoListAgentDirectWorkPhone' },
            {
              text: 'List Office',
              value: this.contactStrings(
                '/',
                'ListOfficeMLSID',
                'ListOfficeName'
              )
            },
            { text: 'Office Phone ', key: 'ListOfficePhone' },
            { text: 'Agent Fax', key: 'ListingAgentFax' },
            { text: 'Agent Email', key: 'ListAgentEmail' },
            {
              text: 'Sub Agent Amount',
              value: this.contactStrings('', 'SubAgentAmount', 'SubAgent')
            },
            {
              text: 'Lease Agent Amount',
              value: this.contactStrings(
                '',
                'LeaseAgentCompAmount',
                'LeaseAgentComp'
              )
            },
            { text: 'ADOM', key: 'DOM' },
            { text: 'CDOM', key: 'CDOM' },
            {
              text: 'Original List Price',
              value: this.getAmountValue('OriginalListPrice')
            },
            { text: 'Intermediary', value: this.getYesNo('Intermediary') },
            {
              text: 'Variable Rate Commission',
              value: this.getYesNo('VariableRateCommission')
            }
          ]
        default:
          return null
      }
      return returnValue
    },

    getPropertyTopSection() {
      let topSectionValues = [
        { text: 'MLS Number', key: 'ListingId' },
        {
          text: `${this.isLeaseProperty ? 'Property Rent' : 'List Price'}`,
          value: this.getAmountValue('ListPrice')
        },
        { text: 'Bed Bath Count', value: this.getBedBathCount() },
        // { text: 'Living Room(s)', key: 'NumLiving' },
        // { text: 'Dining Room(s)', key: 'NumDining' },
        { text: 'Area', value: this.getArea() },
        { text: 'Type', key: 'PropertyType' },
        {
          text: 'Price Per sqft',
          value: this.formatAmount(this.parseNumber('ListPrice') / this.parseNumber('LivingArea')) // 'RATIO_CurrentPrice_By_SQFT')
        },
        { text: 'Status', value: this.getPropertyStatus() },
        { text: 'Last Modified', value: this.getDate('ModificationTimestamp') }
      ]
      if (this.isLeaseProperty) {
        topSectionValues.push(
          ...[
            { text: 'Housing Voucher', value: this.getYesNo('Section8') },
            { text: 'Occupant Type', key: 'OccupantType' },
            { text: 'Occupant Phone', key: 'OccupantPhone' },
            {
              text: 'Availability',
              value: this.getDate('OriginalEntryTimestamp')
            }
          ]
        )
      }
      return topSectionValues
    },

    getPropertyStatus() {
      return this.isLeaseProperty
        ? `${this.homeInfo.Status}/LSE`
        : this.homeInfo.Status
    },

    getDate(key) {
      if (this.homeInfo[key]) {
        return moment(this.homeInfo[key]).format('MM-DD-YYYY')
      }
      return ''
    },
    parseNumber(key) {
      if (this.homeInfo[key]) {
        return parseInt(this.homeInfo[key])
      }
      return 0
    },
    getBedBathCount() {
      return this.homeInfo['BedroomsTotal'] + ' / ' + this.homeInfo['BathroomsFull']
    },
    getYesNo(key) {
      if (this.homeInfo[key] && this.homeInfo[key] === '1') {
        return 'Yes'
      }
      if (this.homeInfo[key] && this.homeInfo[key] === true) {
        return 'Yes'
      }
      return 'No'
    },
    contactStrings(separator, ...items) {
      return items.reduce((result, item, index) => {
        if (index !== 0 && item.value) {
          result += `${separator}${item.value}`
          return result
        }
        if (index !== 0 && this.homeInfo[item]) {
          result += `${separator}${this.homeInfo[item]}`
          return result
        }
        if (item.value) {
          return item.value
        }
        return `${this.homeInfo[item] ? this.homeInfo[item] : ''}`
      }, '')
    },
    formatAmount(amt) {
      const decimalValue = Math.round(parseFloat(amt))
      return (
        '$ ' + decimalValue.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')
      )
    },
    getAmountValue(key) {
      const val = this.homeInfo[key]
      if (isEmpty(val)) return ''
      return this.formatAmount(val)
    },
    getArea() {
      // return parseFloat(this.homeInfo['SqftTotal']).toFixed(2) + ' Sq Ft'
      const decimalValue = Math.round(parseFloat(this.homeInfo['LivingArea']))
      return (
        decimalValue.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',') + ' Sq Ft'
      )
    },
    getStatusClass(status) {
      if (status === 'Active' || status === 'Active Contingent') {
        return 'green-status'
      }
      if (status === 'Pending' || status === 'Pending - Taking Backups') {
        return 'yellow-status'
      }
      return ''
    },
    getTaxRate() {
      return this.homeInfo.TaxRate
        ? parseFloat(this.homeInfo['TaxRate']).toFixed(4) + '%'
        : ''
    }
  }
}
</script>

<style lang="sass">
.green-status
  color: $hwayzGreen
.yellow-status
  color: $hwayzYellow
.v-carousel__next,
.v-carousel__prev
  background: $hwayzBlack

.hidden-icons
  .v-carousel__next,
  .v-carousel__prev
    display: none !important
</style>

<style lang="sass" scoped>
.corner-strip
  width: 15rem
  height: 15rem
  overflow: hidden
  position: absolute
  top: -1rem
  left: -1rem
  z-index: 9
  &:before, &:after
    position: absolute
    z-index: -1
    content: ''
    display: block
    border: 0.5rem solid $hwayzBrandBlue
    border-top-color: transparent
    border-left-color: transparent
  &:before
    top: 0
    right: 0
  &:after
    bottom: 0
    left: 0
  span
    position: absolute
    display: block
    width: 22.5rem
    padding: 1.5rem 0
    background-color: $hwayzBrandBlue
    box-shadow: 0 0.5rem 1rem rgba(0,0,0,.1)
    color: $hwayzWhite
    text-align: center
    right: -2.5rem
    top: 3rem
    transform: rotate(-45deg)
  @media only screen and (max-width: 600px)
    top: 0
    left: 0

.corner-text
  position: absolute
  top: 1rem
  left: 1rem
  color: $hwayzBorder
  font-size: 1.3rem
  z-index: 2
  text-align: center
  transform: rotate(-45deg)

#detailPropertyView
  padding-top: 3rem
  @media only screen and (max-width: 1264px) and (min-width: 960px)
    &.container
      max-width: 1200px !important

.detail-property-view__title
  font-weight: 400
  text-align: left

.detail-property-view__assign-to
  text-align: left
  float: left
  font-weight: 600
  font-size: 13px
  padding: 0 24px
  padding-left: 2rem
  line-height: 4rem
  width: 40%

.action-button
  margin: 2rem 1rem 0
  width: 20rem
  @media only screen and (max-width: 960px)
    margin-bottom: 1rem

.imageWrap
  height: 100%
  grid-area: image
  display: grid
  grid-template-areas: "image" "insideImage" "buttonWrapper"
  grid-template-columns: 1fr

.status-info
  font-size: 18px

.image
  max-width: 100%
  grid-area: image
  position: relative
  height: 400px
  margin-bottom: 2rem
  @media only screen and (max-width: 600px)
    padding: 0 1rem
    margin-top: 1rem

.inside-image
  grid-area: insideImage
  display: grid
  grid-template-areas: "favorite request"
  grid-template-columns: 1fr 1fr
  justify-items: center
  align-items: center

.button-wrapper
  grid-area: buttonWrapper

#textCard
  position: absolute
  left: 0
  bottom: 0
  height: 4rem
  width: 100%
  @media only screen and (max-width: 600px)
    width: calc(100% - 2rem)
    left: 1rem

.agent-fix
  bottom: 0
  @media only screen and (min-width: 1264px)
    bottom: 16px

.client-fix
  bottom: 0

.image-number-indicator
  color: $hwayzWhite
  text-align: center
  line-height: 4rem

.favorite
  grid-area: favorite
  cursor: pointer
  i
    font-size: 3rem !important
    color: $hwayzRed !important
  &.fav-standard
    i
      color: $hwayzDarkerGray !important
    &:hover
      color: $hwayzBrandGold !important
      i
        color: $hwayzRed !important

.request
  grid-area: request
  i
    font-size: 3rem !important
    color: $hwayzDarkenGreen !important
  &.property-standard
    cursor: pointer
    i
      color: $hwayzDarkerGray !important
    &:hover, &:hover i
      color: $hwayzDarkenGreen !important

.property-requested
  @extend %disable-selection
  cursor: default !important
  pointer-events: none

.information
  grid-area: information
  height: 100%
  @media only screen and (max-width: 960px)
    margin-top: 4rem
.sections
  grid-area: sections
  margin-bottom: 4rem
.map
  grid-area: map
  @media only screen and (max-width: 960px)
    margin-top: 4rem
    margin-bottom: 4rem
.back
  grid-area: back
.main-grid
  display: grid
  grid-template-areas: "image information" "sections information" "sections map"
  grid-row-gap: 0.5%
  grid-template-columns: 4fr 2fr
  &.not-rent
    grid-template-areas: "image information" "sections map"

@media only screen and (min-width: 960px)
  .main-grid
    grid-column-gap: 2%
    grid-template-columns: 3fr 2fr
    grid-template-rows: minmax(425px, 540px) minmax(10px, 150px) auto
    &.not-rent
      grid-template-rows: minmax(425px, 550px) auto

@media only screen and (min-width: 1264px)
  .information
    padding: 2%
  .map
    padding: 2%

@media only screen and (max-width: 960px)
  .main-grid
    display: inline

@media only screen and (max-width: 600px)
  .container
    padding: 0

  .detail-property-view__title
    text-align: center
    margin: 1rem 1rem 2rem 1rem
  #detailPropertyView
    padding-top: 0
</style>
